import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SortableTableHead } from "../components/SortableTableHead";
import { TablePagination } from "../components/TablePagination";
import { selectClass } from "../components/FormField";
import { enterpriseApprovedInvoices } from "../data";
import { useEnterpriseInvoices, type EnterpriseInvoiceRow } from "../hooks/useEnterpriseInvoices";
import { companyBillingDetailPath } from "../lib/companyBillingPaths";
import { InvoiceNatureBadge } from "../components/InvoiceNatureBadge";
import { getAuthToken } from "../auth";
import type { Invoice } from "../types";

function mapDemoApproved(invoice: Invoice): EnterpriseInvoiceRow {
  return {
    id: invoice.id,
    displayId: invoice.displayId,
    reference: invoice.reference,
    hospital: invoice.hospital,
    company: invoice.company,
    fileName: invoice.fileName,
    amount: invoice.amount,
    submittedDate: invoice.submittedDate,
    period: invoice.period,
    displayStatus: "approved",
    invoiceNature: invoice.invoiceNature ?? "hors_forfait",
    isForfaitMensuel: invoice.isForfaitMensuel ?? false,
  };
}

export function EnterpriseApprovedInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.enterpriseApproved;
  const pageTitle = t.safecheck.nav.approvedInvoicesToPay;
  const { invoices, loading, error, usingApi } = useEnterpriseInvoices({ status: "approved" });

  const sourceInvoices = useMemo(() => {
    if (!getAuthToken()) {
      return enterpriseApprovedInvoices.map(mapDemoApproved);
    }
    return invoices;
  }, [invoices]);

  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("displayId", "desc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const filtered = useMemo(() => {
    return sourceInvoices.filter((invoice) => {
      if (!tableSearch) return true;
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.reference} ${invoice.fileName}`.toLowerCase();
      return haystack.includes(tableSearch.toLowerCase());
    });
  }, [sourceInvoices, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (invoice, key) => {
        if (key === "displayId") return invoice.displayId;
        if (key === "hospital") return invoice.hospital;
        if (key === "company") return invoice.company;
        if (key === "invoice") return invoice.fileName;
        if (key === "status") return labels.status;
        return "";
      }),
    [filtered, sortKey, sortDirection, labels.status],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const columnDefs = [
    { key: "displayId", label: labels.columns.id, sortable: true },
    { key: "hospital", label: labels.columns.hospital, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "nature", label: labels.columns.nature, sortable: true },
    { key: "invoice", label: labels.columns.invoice, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/enterprise"
        items={[
          { label: t.safecheck.nav.invoices },
          { label: pageTitle },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>
          {usingApi && !loading && (
            <p className="mt-1 text-xs text-muted">
              {sorted.length} facture{sorted.length > 1 ? "s" : ""} approuvée
              {sorted.length > 1 ? "s" : ""}
            </p>
          )}
          {loading && <p className="mt-1 text-xs text-muted">Chargement…</p>}
          {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    <Link
                      to={companyBillingDetailPath(invoice.displayId)}
                      className="text-primary transition-colors hover:text-primary/80"
                    >
                      {invoice.displayId}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{invoice.company}</td>
                  <td className="px-4 py-3">
                    <InvoiceNatureBadge
                      nature={invoice.invoiceNature}
                      isForfaitMensuel={invoice.isForfaitMensuel}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={companyBillingDetailPath(invoice.displayId)}
                      className="text-sm font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      {labels.download}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-green/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-green">
                      {labels.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={companyBillingDetailPath(invoice.displayId)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-card-muted"
                    >
                      <Eye size={14} />
                      {labels.actions.view}
                    </Link>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={setPage}
          labels={labels.pagination}
        />
      </div>
    </>
  );
}
